import { useEffect, useState } from 'react';
import {
  Box,
  Chip,
  CircularProgress,
  Paper,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Typography,
} from '@mui/material';
import api from '../../api/axios';

interface Column {
  name: string;
  type: string;
  nullable: boolean;
  default: string | null;
}

interface Dataset {
  dataset_id: string;
  display_name: string;
  description: string;
  status: string;
  data_source_type: string;
  connection_info: { database?: string; table?: string; kind?: string };
  related_archetypes: string[];
  row_count: number | null;
  last_refreshed_at: string | null;
  schema?: { columns: Column[] } | null;
}

const DatasetDetail = ({ datasetId }: { datasetId: string }) => {
  const [ds, setDs] = useState<Dataset | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [sample, setSample] = useState<any | null>(null);
  const [sampleLoading, setSampleLoading] = useState(false);
  const [sampleError, setSampleError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setDs(null);
    setSample(null);
    api
      .get(`/explore/datasets/${datasetId}/`)
      .then((res) => { if (!cancelled) setDs(res.data); })
      .catch((err) => {
        if (!cancelled) setError(err?.response?.data?.error || err.message || 'failed');
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [datasetId]);

  const loadSample = async () => {
    setSampleLoading(true);
    setSampleError(null);
    try {
      const res = await api.get(`/explore/datasets/${datasetId}/sample/`, { params: { limit: 5 } });
      setSample(res.data);
    } catch (err: any) {
      setSampleError(err?.response?.data?.error || err.message || 'failed');
    } finally { setSampleLoading(false); }
  };

  if (loading) return <Box sx={{ p: 4, display: 'flex', justifyContent: 'center' }}><CircularProgress /></Box>;
  if (error || !ds) return <Box sx={{ p: 4 }}><Typography color="error">{error || 'Not found'}</Typography></Box>;

  return (
    <Box>
      <Box sx={{ p: 3, pb: 2, borderBottom: 1, borderColor: 'divider' }}>
        <Typography variant="h5" sx={{ fontWeight: 600 }}>{ds.display_name || ds.dataset_id}</Typography>
        <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace' }}>{ds.dataset_id}</Typography>
        <Box sx={{ display: 'flex', gap: 0.75, mt: 1.5, flexWrap: 'wrap' }}>
          <Chip label={ds.status} size="small" />
          <Chip label={ds.data_source_type} size="small" variant="outlined" />
          {ds.connection_info?.kind === 'view' && <Chip label="view" size="small" variant="outlined" />}
        </Box>
      </Box>

      <Box sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, maxWidth: 920 }}>
          {ds.description && (
            <Section title="Description">
              <Typography variant="body1">{ds.description}</Typography>
            </Section>
          )}

          <Section title="Connection">
            <Box sx={{ display: 'grid', gridTemplateColumns: '140px 1fr', rowGap: 0.5, columnGap: 2 }}>
              <Typography variant="body2" color="text.secondary">Database</Typography>
              <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>{ds.connection_info?.database || '—'}</Typography>
              <Typography variant="body2" color="text.secondary">Table</Typography>
              <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>{ds.connection_info?.table || '—'}</Typography>
              <Typography variant="body2" color="text.secondary">Rows</Typography>
              <Typography variant="body2">{ds.row_count?.toLocaleString() || '—'}</Typography>
              <Typography variant="body2" color="text.secondary">Last refreshed</Typography>
              <Typography variant="body2">{ds.last_refreshed_at ? new Date(ds.last_refreshed_at).toLocaleString() : '—'}</Typography>
            </Box>
          </Section>

          {ds.related_archetypes?.length > 0 && (
            <Section title="Related archetypes">
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {ds.related_archetypes.map((a) => (
                  <Chip key={a} label={a} size="small" variant="outlined" />
                ))}
              </Box>
            </Section>
          )}

          {ds.schema?.columns && (
            <Section title={`Schema (${ds.schema.columns.length} columns)`}>
              <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 360 }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell>Name</TableCell>
                      <TableCell>Type</TableCell>
                      <TableCell>Null</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {ds.schema.columns.map((c) => (
                      <TableRow key={c.name}>
                        <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>{c.name}</TableCell>
                        <TableCell sx={{ fontSize: '0.8rem' }}>{c.type}</TableCell>
                        <TableCell sx={{ fontSize: '0.8rem' }}>{c.nullable ? 'Y' : 'N'}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Section>
          )}

          <Section title="Sample rows">
            {!sample && !sampleLoading && (
              <Typography variant="body2" color="text.secondary" sx={{ cursor: 'pointer', textDecoration: 'underline' }} onClick={loadSample}>
                Load 5 sample rows
              </Typography>
            )}
            {sampleLoading && <CircularProgress size={20} />}
            {sampleError && <Typography color="error" variant="body2">{sampleError}</Typography>}
            {sample?.rows?.length > 0 && (
              <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 360 }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      {sample.columns.map((c: string) => <TableCell key={c}>{c}</TableCell>)}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {sample.rows.map((r: any, i: number) => (
                      <TableRow key={i}>
                        {sample.columns.map((c: string) => (
                          <TableCell key={c} sx={{ fontSize: '0.75rem', fontFamily: 'monospace', maxWidth: 240, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {r[c] === null ? '—' : String(r[c])}
                          </TableCell>
                        ))}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Section>
        </Box>
      </Box>
    </Box>
  );
};

const Section = ({ title, children }: { title: string; children: React.ReactNode }) => (
  <Box>
    <Typography variant="overline" color="text.secondary" sx={{ display: 'block', mb: 1 }}>{title}</Typography>
    {children}
  </Box>
);

export default DatasetDetail;
